<div class="flex flex-col flex-1 px-3 py-2 max-w-5xl mx-auto w-full">
  <div class="flex items-center mb-2">
    <h1 class="text-base font-semibold">Ticket Templates</h1>
    @if (mode === 'list') {
      <div class="ml-auto flex gap-2">
        <button class="btn btn-sm btn-outline btn-info" (click)="refresh()" [disabled]="loading">
          <i class="ri-refresh-line"></i>
          Refresh
        </button>
        <button class="btn btn-sm btn-primary" (click)="startCreate()">
          <i class="ri-add-line"></i>
          New skill
        </button>
      </div>
    } @else {
      <button class="ml-auto btn btn-sm btn-outline" (click)="cancelEdit()">
        <i class="ri-arrow-left-line"></i>
        Back to list
      </button>
    }
  </div>

  @if (listError) {
    <div role="alert" class="alert alert-error text-sm mb-2">
      <i class="ri-error-warning-line"></i>
      <span>{{ listError }}</span>
    </div>
  }

  @if (mode === 'list') {
    <div class="bg-white shadow-sm rounded-md overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-100">
          <tr>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Description
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Enabled
            </th>
            <th
              class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Version
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Updated
            </th>
            <th class="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          @if (loading) {
            <tr><td colspan="6" class="px-3 py-6 text-center text-gray-400">Loading…</td></tr>
          } @else if (skills.length === 0) {
            <tr>
              <td colspan="6" class="px-3 py-6 text-center text-gray-500">
                No skills yet.
                <button class="link text-primary underline" (click)="startCreate()">
                  Create the first one
                </button>
                .
              </td>
            </tr>
          } @else {
            @for (s of skills; track s.id) {
              <tr class="hover:bg-gray-50">
                <td class="px-3 py-2 font-medium">{{ s.name }}</td>
                <td class="px-3 py-2 text-gray-600 max-w-[40ch] truncate" [title]="s.description">
                  {{ s.description || '—' }}
                </td>
                <td class="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    [checked]="s.enabled"
                    (change)="toggleEnabled(s)"
                    title="Toggle enabled"
                  />
                </td>
                <td class="px-3 py-2 text-right tabular-nums">v{{ s.version }}</td>
                <td class="px-3 py-2 text-xs text-gray-500">{{ s.updated_at | date: 'short' }}</td>
                <td class="px-3 py-2 whitespace-nowrap text-right">
                  <button class="btn btn-xs btn-ghost" (click)="startEdit(s)">
                    <i class="ri-edit-line"></i>
                    Edit
                  </button>
                  <button
                    class="btn btn-xs btn-ghost"
                    (click)="toggleDisabled(s)"
                    [title]="s.enabled ? 'Disable (keeps cached tickets)' : 'Re-enable'"
                  >
                    @if (s.enabled) {
                      <i class="ri-pause-circle-line"></i>
                      Disable
                    } @else {
                      <i class="ri-play-circle-line"></i>
                      Enable
                    }
                  </button>
                  <button
                    class="btn btn-xs btn-error btn-outline"
                    (click)="deletePermanently(s)"
                    title="Permanently delete this template"
                  >
                    <i class="ri-delete-bin-line"></i>
                    Delete permanently
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
    <p class="text-xs text-gray-400 mt-2">
      <b>Disable</b>
      hides the template from AI Ticket while keeping the row and any cached tickets — reversible
      via
      <b>Enable</b>
      .
      <b>Delete permanently</b>
      drops the row outright and cannot be undone.
    </p>
  } @else {
    <!-- EDIT / CREATE FORM -->
    <div class="bg-white shadow-sm rounded-md p-4 space-y-4">
      @if (saveError) {
        <div role="alert" class="alert alert-error text-sm">
          <i class="ri-error-warning-line"></i>
          <span>{{ saveError }}</span>
        </div>
      }

      <div class="grid md:grid-cols-2 gap-4">
        <div>
          <label class="text-sm font-medium text-gray-700" for="skill-name">Name</label>
          <input
            id="skill-name"
            class="mt-1 w-full pl-3 pr-3 py-1 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
            [(ngModel)]="editing.name"
            placeholder="e.g. Carrier Incident Report"
            [disabled]="saving"
          />
        </div>
        <div class="flex items-end gap-2">
          <label class="text-sm font-medium text-gray-700 flex items-center gap-2">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              [(ngModel)]="editing.enabled"
              [disabled]="saving"
            />
            Enabled
          </label>
        </div>
      </div>

      <div>
        <label class="text-sm font-medium text-gray-700" for="skill-desc">
          Description
          <span class="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          id="skill-desc"
          class="mt-1 w-full pl-3 pr-3 py-1 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
          [(ngModel)]="editing.description"
          [disabled]="saving"
        />
      </div>

      <div>
        <label class="text-sm font-medium text-gray-700" for="skill-prompt">System prompt</label>
        <textarea
          id="skill-prompt"
          rows="10"
          class="mt-1 w-full pl-3 pr-3 py-2 text-sm font-mono rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
          [(ngModel)]="editing.system_prompt"
          [disabled]="saving"
          placeholder="You are a …"
        ></textarea>
      </div>

      <div>
        <label class="text-sm font-medium text-gray-700" for="skill-schema">
          Output schema
          <span class="text-gray-400 font-normal">(JSON Schema subset)</span>
        </label>
        <textarea
          id="skill-schema"
          rows="14"
          class="mt-1 w-full pl-3 pr-3 py-2 text-xs font-mono rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
          [(ngModel)]="schemaText"
          [disabled]="saving"
        ></textarea>
        <p class="text-xs text-gray-400 mt-1">
          Supported:
          <code>type</code>
          (object/array/string/number/integer/boolean),
          <code>properties</code>
          ,
          <code>required</code>
          ,
          <code>items</code>
          ,
          <code>enum</code>
          .
        </p>
      </div>

      <div class="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
        @if (hasUnsavedChanges()) {
          <span class="badge badge-sm badge-ghost gap-1 mr-auto" title="You have unsaved edits">
            <i class="ri-error-warning-line"></i>
            Unsaved changes
          </span>
        }
        <button class="btn btn-sm btn-ghost" (click)="cancelEdit()" [disabled]="saving">
          Cancel
        </button>
        <button class="btn btn-sm btn-primary" (click)="save()" [disabled]="saving">
          @if (saving) {
            <span class="loading loading-spinner loading-xs mr-1"></span>
          } @else {
            <i class="ri-save-line mr-1"></i>
          }
          {{ mode === 'edit' ? 'Save changes' : 'Create skill' }}
        </button>
      </div>
    </div>
  }
</div>
